import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import isArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import {assertAndContinue} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {FeatureDefinition} from 'auth/feature-enablement/FeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {ActionOrigin, ProcessPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {openArchiveModalForProcessItems} from 'processes/archive/archiveUtil';
import {BulkArchiveResponse} from 'processes/dao/ProcessManagementDAO';
import ExecuteButton from 'processes/dashboard/ExecuteButton';
import {
  getExecuteTooltipMessage,
  isDirectExecutionTaskWithNoOutputSchema,
} from 'processes/dashboard/ExecuteTasksButton';
import CopyTasksConfirmationModal from 'processes/modals/CopyTasksConfirmationModal';
import {
  isProcessPlatformFeatureEnabledAndHasCapability,
  getProcessPlatformFeatureConfig,
} from 'processes/processes-platform/FeatureOptions';
import {ButtonSize} from 'ui-library/buttons/Button';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

import * as ProcessManagementActions from '../../ProcessManagementActions';
import {ProcessTaskDetailsQuery} from '../graphql/__generated/ProcessTaskDetailsQuery';
import {UpdateTaskWithOutputsParams} from '../utils/updateTaskDetailsOutputs';
import {UpdateCachedTaskParams} from '../utils/updateTaskDetailsTask';

type ProcessTaskGql = ExtractByTypename<ProcessTaskDetailsQuery['processTask'], 'ProcessTaskNode'>;

interface ProcessActionToolbarProps {
  processTask: ProcessTaskGql;
  onTaskUpdated: () => Promise<unknown>;
  updateCache: (updatedTask: UpdateCachedTaskParams) => void;
  updateOutputsCache: (updateTaskOutputParams: UpdateTaskWithOutputsParams) => void;
}

interface OptionPermissionData {
  isDisabled: boolean;
  tooltipTitle?: string;
}

function archiveProcessTask(
  processTask: ProcessTaskGql,
  refetch: () => Promise<unknown>,
  updateOutputsCache: (updateTaskOutputParams: UpdateTaskWithOutputsParams) => void
): void {
  openArchiveModalForProcessItems({
    itemType: ModelTypeConstants.PROCESS_TASK,
    items: [processTask],
    afterArchive: (archiveResponse: BulkArchiveResponse, purpose) => {
      const archivedOutputIds = archiveResponse.archivedItemIds.filter((id: string) =>
        APIUtil.isProcessOutput(id)
      );
      updateOutputsCache({
        taskId: processTask.id,
        updatedStatusAndOutputs: {},
        archivedOutputs: archivedOutputIds,
        purpose,
      });
      refetch();
    },
  });
}

async function unarchiveProcessTask(
  processTask: ProcessTaskGql,
  refetch: () => Promise<unknown>,
  updateOutputsCache: (updateTaskOutputParams: UpdateTaskWithOutputsParams) => void
): Promise<void> {
  const doUnarchive = async (): Promise<void> => {
    const response = await ArchiveActions.bulkUnarchiveProcessManagement(ModelTypeConstants.PROCESS_TASK, [
      processTask.id,
    ]);
    if (response) {
      const unarchivedOutputIds = response.unarchivedItemIds.filter((id) => APIUtil.isProcessOutput(id));
      updateOutputsCache({
        taskId: processTask.id,
        updatedStatusAndOutputs: {},
        unarchivedOutputs: unarchivedOutputIds,
      });
      await refetch();
    }
  };
  await withLoadingScreen(doUnarchive(), 'Unarchiving task...');
}

// Stubbable functions to mock for testing purposes
export const stubbableFunctions = {
  getOptionDisabledAndTooltip(
    feature: FeatureDefinition,
    isProcessPlatformFeatureEnabled?: boolean
  ): OptionPermissionData {
    const isDisabled =
      !isFeatureEnabled(feature) ||
      (isProcessPlatformFeatureEnabled != null && !isProcessPlatformFeatureEnabled);
    const tooltipMissingPermissions = "You don't have permissions to perform this action.";
    return {
      isDisabled,
      tooltipTitle: isDisabled ? tooltipMissingPermissions : undefined,
    };
  },
};

export default function ProcessTaskActionToolbar(props: ProcessActionToolbarProps): JSX.Element {
  const processTask = props.processTask;
  const executeTooltipMessage = getExecuteTooltipMessage([processTask]);

  const showExecuteButton =
    isProcessPlatformFeatureEnabledAndHasCapability(
      ProcessPlatformFeatureType.IS_DIRECTLY_EXECUTABLE,
      props.processTask.schema.executionType,
      props.processTask.schema.clientOrigin,
      props.processTask.schema.name
    ) && !isDirectExecutionTaskWithNoOutputSchema(processTask.schema);

  const overflowMenuOptions = [
    {
      isVisible: getProcessPlatformFeatureConfig(
        ProcessPlatformFeatureType.CAN_COPY_TASKS,
        props.processTask.schema.executionType,
        props.processTask.schema.name
      ),
      item: {
        contents: <span>Copy Task</span>,
        ...stubbableFunctions.getOptionDisabledAndTooltip({feature: ProcessesFeature.PROCESSES_CREATE}),
        onSelect: async () => {
          let copiedTask;
          if (processTask.assignee != null && processTask.schema.canSetAssigneeOnTaskCreation === false) {
            copiedTask = await runModal(CopyTasksConfirmationModal, {taskIds: [processTask.id]});
          } else {
            copiedTask = await ProcessManagementActions.copyTasks([processTask.id]);
          }
          if (copiedTask == null) {
            return;
          }
          assertAndContinue(copiedTask.length === 1, 'Expected to only copy one task.');
          // Open the newly created task.
          FileBrowserActions.openFile(copiedTask[0].id);
        },
      },
    },
    {
      isVisible: isArchivable(processTask) && !isArchived(processTask),
      item: {
        ...stubbableFunctions.getOptionDisabledAndTooltip({
          feature: ProcessesFeature.PROCESSES_ARCHIVE,
          item: processTask,
        }),
        contents: <span>Archive Task</span>,
        onSelect: () => {
          archiveProcessTask(processTask, props.onTaskUpdated, props.updateOutputsCache);
        },
      },
    },
    {
      isVisible: isArchivable(processTask) && isArchived(processTask),
      item: {
        ...stubbableFunctions.getOptionDisabledAndTooltip({
          feature: ProcessesFeature.PROCESSES_UNARCHIVE,
          item: processTask,
        }),
        contents: <span>Unarchive Task</span>,
        onSelect: async () => {
          await unarchiveProcessTask(processTask, props.onTaskUpdated, props.updateOutputsCache);
        },
      },
    },
    {
      isVisible: true,
      item: {
        ...stubbableFunctions.getOptionDisabledAndTooltip({feature: ProcessesFeature.PROCESSES_EXPORT}),
        contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
        onSelect: () => {
          AuditActions.exportAuditLogs([processTask.id]);
        },
        dataTestAttrs: {component: 'ProcessTaskActionToolbar', element: 'exportAuditLog'},
      },
    },
  ]
    .filter(({isVisible}) => isVisible)
    .map(({item}) => item);

  return (
    <div className={css(styles.toolbarWrapper)}>
      {overflowMenuOptions.length > 0 && (
        <OverflowMenu
          buttonProps={{size: ButtonSize.SMALL, className: css(styles.overflowButton)}}
          menuProps={{dropLeft: true}}
          options={overflowMenuOptions}
        />
      )}
      {showExecuteButton && (
        <div className={classnames('buttonGroup')}>
          <ExecuteButton
            actionOrigin={ActionOrigin.WORKFLOWS_TASK_DETAILS_PAGE}
            getTasksToExecute={() => Promise.resolve([processTask])}
            disabledMessage={executeTooltipMessage}
            onTasksOrOutputsChange={(tasks, outputs) => {
              if (!assertAndContinue(tasks.length === 1, 'Expected to have only updated one task')) {
                return;
              }
              const updatedTask = tasks[0];
              if (
                !assertAndContinue(
                  updatedTask.id === processTask.id,
                  `Updated task does not match. Expected task id: ${processTask.id}. Received task id: ${updatedTask.id}`
                )
              ) {
                return;
              }
              props.updateCache({
                id: processTask.id,
                status: updatedTask.status,
                executionOrigin: updatedTask.executionOrigin,
              });
              props.updateOutputsCache({
                taskId: processTask.id,
                updatedStatusAndOutputs: {
                  outputs: outputs ?? [],
                  status: updatedTask.status,
                },
              });
            }}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  toolbarWrapper: {
    display: 'flex',
    gap: '8px',
  },
  overflowButton: {
    height: '28px',
    width: '28px',
  },
});
